import type english from '@/i18n/en-us'
import type { TPanel, TRightPanel } from '@lifk/types'

export type TTutorialPlacement = 'top' | 'bottom' | 'left' | 'right'

// Copy lives in i18n/<lang>/ui.json under TutorialOverlay — these keys tie each
// step and tutorial to its entry there, so a missing string is a type error.
export type TTutorialStepId = keyof typeof english.TutorialOverlay.steps
export type TTutorialI18nKey = keyof typeof english.TutorialOverlay.finale &
  keyof typeof english.ChooseTutorialModal

export type TTutorialGate =
  | { type: 'manual' } // advances via a "Got it" button (aha / show-only steps)
  | { type: 'subtools-open' }
  | { type: 'site-selected' } // a new site is created, or an existing one is picked
  | { type: 'left-panel'; panel: TPanel }
  | { type: 'active-tool'; tool: TRightPanel }
  | { type: 'remedy-placed' }
  | { type: 'comment-added' }
  | { type: 'comment-draft-placed' }
  | { type: 'draft-category-menu-open' }
  | { type: 'draft-category-selected' }
  | { type: 'comment-focused' }
  | { type: 'layer-loaded-named'; name: string }
  | { type: 'layer-loaded-any' }
  | { type: 'data-readout' }
  | { type: 'data-readout-closed' }
  | { type: 'left-panel-closed' }
  | { type: 'references-open' }
  | { type: 'references-closed' }

export interface ITutorialDecoration {
  src: string
  corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  size: number
}

export type TTutorialMapTarget = 'selected-site'

export interface ITutorialStep {
  id: TTutorialStepId
  selector: string // '' = map step (no target, dim suppressed)
  mapTarget?: TTutorialMapTarget // spotlight a projected map feature rather than an element
  fitMapTarget?: boolean // frame the map on that feature so it fills the viewport
  inlineIcon?: string // svg shown inline where `{icon}` appears in the step's body copy
  placement?: TTutorialPlacement // defaults to 'top'
  gate: TTutorialGate
  allowSelectors?: string[]
  hideSelectors?: string[] // take a control out of play for the step's duration
  optional?: boolean
  hideDim?: boolean
  blackout?: boolean // dim the whole viewport on a step with nothing to spotlight
  cardAnchor?: { selector: string; offset?: number }
  unfocusComment?: boolean
  decoration: ITutorialDecoration
  spotlightPadding?: number // defaults to 8
  flyTo?: { center: [number, number]; zoom: number; always?: boolean }
  parkAt?: 'top-right' | 'left-center' | 'bottom-right' | 'center' // fixed card position instead of anchoring to the target
  revealTarget?: boolean // force a hover-hidden target visible while highlighted
  closeReferences?: boolean // close the open guidebook on entry
  openAccountMenu?: boolean // closer: auto-open the account menu to reveal the tutorials button
  backOnCategoryClose?: boolean // return to the previous step if the category menu is dismissed without a pick
}

export interface ITutorial {
  id: string
  i18nKey: TTutorialI18nKey
  icon: string // sticker shown on the picker card
  hidden?: boolean // reachable by id (auto-start, deep link) but absent from the picker
  preservesWorkspace?: boolean
  steps: ITutorialStep[]
}

const STICKER = '/images/prompts'
const ANIMAL = (n: string) => `${STICKER}/Animals/${n}-no-back.svg`
const PLANT = (n: string) => `${STICKER}/Plants/${n}-no-back.svg`
const OBJECT = (n: string) => `${STICKER}/Objects/${n}-no-back.svg`
const TOOL_ICON = (n: string) => `/images/toolTrayIcons/${n}.svg`

const MAP_CANVAS = '#mapbox'
const DATA_INSIGHTS_PANEL = '#panel-data-insights'
const GUIDEBOOK_BUTTON = '[data-tour="guidebook"]'
const MAP_TOOLS = '[data-tour="map-tools"]'
const COMMENT_POPUP = '[data-testid="ui-body-popup"]'
const CATEGORY_TRIGGER = `${COMMENT_POPUP} [data-tour="comment-category"]`
const CATEGORY_MENU = '#dropdown-menu-contents'

const MAGIC_JOHNSON_PARK: [number, number] = [-118.259972, 33.920056]
const COMMENT_NEED_MARKER = '[data-tour="comment-marker-need"]'
const REMEDY_MARKER = '[data-intercom-target^="remedy-"]'

// Auto-started on first visit to the sample sketch (see MapContents) rather than picked from the tutorials modal
export const SAMPLE_TUTORIAL_ID = 'sample-sketch'

// LayerLibrary builds its `data-tour` anchor from this same CMS display name,
// so a Sanity rename breaks the selector and the gate together
const TOUR_LAYER_NAME = 'Annual Rainfall'
const TOUR_LAYER_SELECTOR = `[data-tour="layer-item-${TOUR_LAYER_NAME}"]`

// Shared opening for the place-based flows: frame LA County, open the tool, click a parcel.
const placeSetupSteps = (): ITutorialStep[] => [
  {
    id: 'open-site-tool',
    selector: '[data-tour="create-site"]',
    inlineIcon: TOOL_ICON('PolygonIcon'),
    gate: { type: 'subtools-open' },
    decoration: {
      src: PLANT('broadleaf-sprout'),
      corner: 'top-left',
      size: 70
    }
  },
  {
    id: 'click-parcel',
    selector: '',
    gate: { type: 'site-selected' },
    allowSelectors: [MAP_CANVAS],
    cardAnchor: { selector: MAP_TOOLS, offset: 12 },
    decoration: { src: PLANT('flower-trio'), corner: 'top-left', size: 76 },
    spotlightPadding: 0,
    flyTo: { center: MAGIC_JOHNSON_PARK, zoom: 15 }
  }
]

// Shared closer: open the account menu and highlight where every tutorial lives.
const findTutorialsStep = (
  close: { references?: boolean } = {}
): ITutorialStep => ({
  id: 'find-tutorials',
  selector: '[data-testid="tutorials-btn"]',
  placement: 'left',
  gate: { type: 'manual' },
  decoration: { src: ANIMAL('bird-branch'), corner: 'bottom-left', size: 74 },
  spotlightPadding: 6,
  closeReferences: close.references,
  openAccountMenu: true
})

export const tutorials: ITutorial[] = [
  {
    id: 'understanding-place',
    i18nKey: 'understandingPlace',
    icon: PLANT('tree'),
    steps: [
      ...placeSetupSteps(),
      {
        id: 'read-data-insights',
        selector: '[data-tour="data-insights-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'data-insights' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-data-panel',
        selector: DATA_INSIGHTS_PANEL,
        placement: 'right',
        gate: { type: 'layer-loaded-any' },
        hideSelectors: [`${DATA_INSIGHTS_PANEL} ${GUIDEBOOK_BUTTON}`],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  },
  {
    id: 'explore-map',
    i18nKey: 'exploreMap',
    icon: OBJECT('water-drops'),
    steps: [
      {
        id: 'open-layers',
        selector: '[data-tour="layer-toggle"]',
        gate: { type: 'active-tool', tool: 'layers' },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        }
      },
      {
        id: 'toggle-rainfall',
        selector: TOUR_LAYER_SELECTOR,
        placement: 'left',
        gate: { type: 'layer-loaded-named', name: TOUR_LAYER_NAME },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        },
        spotlightPadding: 6
      },
      {
        id: 'click-readout',
        selector: '',
        gate: { type: 'data-readout' },
        allowSelectors: [MAP_CANVAS],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'top-left',
          size: 76
        },
        spotlightPadding: 0,
        parkAt: 'left-center'
      },
      {
        id: 'rainfall-aha',
        selector: '[data-tour="data-readout"]',
        placement: 'left',
        gate: { type: 'data-readout-closed' },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 70
        }
      },
      {
        id: 'layer-guidebook',
        selector: `${TOUR_LAYER_SELECTOR} ${GUIDEBOOK_BUTTON}`,
        placement: 'left',
        gate: { type: 'references-open' },
        revealTarget: true,
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-guidebook',
        selector: '[data-tour="guidebook-panel"]',
        placement: 'left',
        gate: { type: 'references-closed' },
        parkAt: 'bottom-right',
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-left',
          size: 72
        },
        spotlightPadding: 0
      },
      findTutorialsStep({ references: true })
    ]
  },
  {
    id: 'create-project',
    i18nKey: 'createProject',
    icon: PLANT('flower-trio'),
    steps: [
      ...placeSetupSteps(),
      {
        id: 'show-site-info',
        selector: '[aria-controls="panel-site-info"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'site-info' },
        decoration: {
          src: PLANT('broadleaf-sprout'),
          corner: 'bottom-right',
          size: 70
        },
        spotlightPadding: 6
      },
      {
        id: 'open-remedies',
        selector: '[data-tour="add-remedy"]',
        gate: { type: 'active-tool', tool: 'remedies' },
        decoration: {
          src: PLANT('broadleaf-sprout'),
          corner: 'top-left',
          size: 70
        }
      },
      {
        id: 'place-remedy',
        selector: '[data-tour="remedy-sticker"]',
        placement: 'left',
        gate: { type: 'remedy-placed' },
        allowSelectors: [MAP_CANVAS],
        hideDim: true,
        decoration: {
          src: PLANT('flower-trio'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 4
      },
      {
        id: 'show-costs-benefits',
        selector: '[data-tour="costs-benefits-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'costs-benefits' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      {
        id: 'read-costs-benefits',
        selector: '#panel-costs-benefits',
        placement: 'right',
        gate: { type: 'left-panel-closed' },
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  },
  {
    id: 'engage-community',
    i18nKey: 'engageCommunity',
    icon: ANIMAL('blue-bird-singing'),
    steps: [
      {
        id: 'open-comments',
        selector: '[data-tour="add-comment"]',
        gate: { type: 'active-tool', tool: 'comments' },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 76
        }
      },
      {
        id: 'drop-comment-pin',
        selector: '',
        gate: { type: 'comment-draft-placed' },
        allowSelectors: [MAP_CANVAS],
        cardAnchor: { selector: MAP_TOOLS, offset: 12 },
        flyTo: { center: MAGIC_JOHNSON_PARK, zoom: 15 },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 72
        },
        spotlightPadding: 0
      },
      {
        id: 'choose-category',
        selector: CATEGORY_TRIGGER,
        placement: 'left',
        gate: { type: 'draft-category-menu-open' },
        allowSelectors: [COMMENT_POPUP],
        cardAnchor: { selector: COMMENT_POPUP },
        hideDim: true,
        decoration: {
          src: PLANT('flower-trio'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'pick-category',
        selector: CATEGORY_MENU,
        placement: 'left',
        gate: { type: 'draft-category-selected' },
        allowSelectors: [CATEGORY_TRIGGER],
        cardAnchor: { selector: COMMENT_POPUP },
        hideDim: true,
        backOnCategoryClose: true,
        decoration: {
          src: PLANT('tree'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'write-comment',
        selector: COMMENT_POPUP,
        placement: 'left',
        gate: { type: 'comment-added' },
        allowSelectors: [COMMENT_POPUP],
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'top-left',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'view-comments',
        selector: '[data-tour="comments-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'comments' },
        decoration: {
          src: ANIMAL('bird-branch'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'open-a-comment',
        selector: '#panel-comments',
        placement: 'right',
        gate: { type: 'comment-focused' },
        unfocusComment: true,
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'bottom-right',
          size: 72
        },
        spotlightPadding: 6
      },
      findTutorialsStep()
    ]
  },
  {
    id: SAMPLE_TUTORIAL_ID,
    i18nKey: 'sampleSketch',
    icon: ANIMAL('kingfisher'),
    hidden: true,
    preservesWorkspace: true,
    steps: [
      {
        id: 'sample-welcome',
        selector: '',
        gate: { type: 'manual' },
        blackout: true,
        parkAt: 'center',
        flyTo: { center: MAGIC_JOHNSON_PARK, zoom: 15, always: true },
        decoration: {
          src: ANIMAL('blue-bird-singing'),
          corner: 'top-left',
          size: 72
        }
      },
      {
        id: 'sample-history',
        selector: '',
        gate: { type: 'manual' },
        blackout: true,
        parkAt: 'center',
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-left',
          size: 66
        }
      },
      {
        id: 'sample-site',
        selector: '',
        mapTarget: 'selected-site',
        fitMapTarget: true,
        placement: 'right',
        gate: { type: 'manual' },
        decoration: { src: PLANT('flower-trio'), corner: 'top-left', size: 76 },
        spotlightPadding: 12
      },
      {
        id: 'sample-comments',
        selector: COMMENT_NEED_MARKER,
        placement: 'right',
        gate: { type: 'manual' },
        decoration: {
          src: ANIMAL('bird-branch'),
          corner: 'bottom-right',
          size: 74
        },
        spotlightPadding: 6
      },
      {
        id: 'sample-opportunities',
        selector: '[data-tour="data-insights-nav"]',
        placement: 'right',
        gate: { type: 'left-panel', panel: 'data-insights' },
        decoration: {
          src: PLANT('broadleaf-sprout'),
          corner: 'bottom-right',
          size: 70
        },
        spotlightPadding: 6
      },
      {
        id: 'sample-opportunities-list',
        selector: '[data-tour="opportunities"]',
        placement: 'right',
        gate: { type: 'manual' },
        decoration: {
          src: OBJECT('water-drops'),
          corner: 'top-right',
          size: 66
        },
        spotlightPadding: 8
      },
      {
        id: 'sample-remedies',
        selector: REMEDY_MARKER,
        placement: 'right',
        gate: { type: 'manual' },
        decoration: {
          src: PLANT('tree'),
          corner: 'top-left',
          size: 72
        },
        spotlightPadding: 8
      },
      {
        id: 'sample-explore',
        selector: '',
        gate: { type: 'manual' },
        blackout: true,
        parkAt: 'center',
        decoration: {
          src: ANIMAL('kingfisher'),
          corner: 'top-left',
          size: 74
        }
      }
    ]
  }
]

/** Decoration shown on the celebration screen after any tutorial. */
export const finaleDecoration: ITutorialDecoration = {
  src: ANIMAL('kingfisher'),
  corner: 'top-right',
  size: 96
}
